import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { courseService, assessmentService } from '../../api';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Plus, Edit2, Trash2, ChevronDown, ChevronRight,
    FileText, Video, File, ArrowLeft, MoreVertical,
    GripVertical, Save, X, Type, Clock, Layout, 
    BookOpen, Layers, CheckCircle2, AlertCircle, ClipboardCheck, Unlink
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import AppSelect from '../../components/ui/AppSelect'

function CourseContent() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [course, setCourse] = useState(null);
    const [subjects, setSubjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedSubjects, setExpandedSubjects] = useState({});
    const [lessonsBySubject, setLessonsBySubject] = useState({});
    const [assessmentsBySubject, setAssessmentsBySubject] = useState({});
    const [courseWideAssessments, setCourseWideAssessments] = useState([]);

    // Modal states
    const [modalConfig, setModalConfig] = useState({
        isOpen: false,
        type: 'subject', // 'subject' | 'lesson' | 'assessment'
        mode: 'create',  // 'create' or 'edit'
        data: null,
        subjectId: null, // parent for lessons / assessments
        suggestedOrder: 1
    });

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const courseRes = await courseService.getCourseDetail(id);
            setCourse(courseRes.data);

            const subjectsRes = await courseService.getSubjects({ course: id });
            const subjectsData = subjectsRes.data.results || subjectsRes.data;
            // Ensure subjects are sorted by order
            const sortedSubjects = [...subjectsData].sort((a, b) => a.order - b.order);
            setSubjects(sortedSubjects);

            // Fetch lessons for all subjects
            const lessonPromises = sortedSubjects.map(s =>
                courseService.getLessons({ subject: s.id })
                    .then(res => ({ 
                        subjectId: s.id, 
                        lessons: (res.data.results || res.data).sort((a, b) => a.order - b.order) 
                    }))
            );

            const lessonsResults = await Promise.all(lessonPromises);
            const lessonsMap = {};
            lessonsResults.forEach(res => {
                lessonsMap[res.subjectId] = res.lessons;
            });
            setLessonsBySubject(lessonsMap);

            // Course-tied assessments (required for completion while active)
            const assessmentsRes = await assessmentService.getAssessments({
                course: id,
                page_size: 100,
            });
            const assessments = assessmentsRes.data.results || assessmentsRes.data || [];
            const bySubject = {};
            const courseWide = [];
            sortedSubjects.forEach((s) => { bySubject[s.id] = []; });
            assessments.forEach((a) => {
                const subjectKey = a.subject || a.subject_id;
                if (subjectKey && bySubject[subjectKey]) {
                    bySubject[subjectKey].push(a);
                } else {
                    courseWide.push(a);
                }
            });
            setAssessmentsBySubject(bySubject);
            setCourseWideAssessments(courseWide);

            // Expand first subject by default if not already set
            if (Object.keys(expandedSubjects).length === 0 && sortedSubjects.length > 0) {
                setExpandedSubjects({ [sortedSubjects[0].id]: true });
            }

        } catch (err) {
            toast.error('Failed to load course content');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const toggleSubject = (subjectId) => {
        setExpandedSubjects(prev => ({
            ...prev,
            [subjectId]: !prev[subjectId]
        }));
    };

    const handleDeleteSubject = async (subjectId) => {
        if (!window.confirm('Delete this subject and all its lessons?')) return;
        try {
            await courseService.deleteSubject(subjectId);
            setSubjects(subjects.filter(s => s.id !== subjectId));
            toast.success('Subject deleted');
        } catch (err) {
            toast.error('Failed to delete subject');
        }
    };

    const handleDeleteLesson = async (lessonId, subjectId) => {
        if (!window.confirm('Delete this lesson?')) return;
        try {
            await courseService.deleteLesson(lessonId);
            setLessonsBySubject(prev => ({
                ...prev,
                [subjectId]: prev[subjectId].filter(l => l.id !== lessonId)
            }));
            toast.success('Lesson deleted');
        } catch (err) {
            toast.error('Failed to delete lesson');
        }
    };

    const handleUnlinkAssessment = async (assessment) => {
        if (!window.confirm(
            `Remove "${assessment.title}" from this course track?\n\n` +
            'It will become standalone practice and will no longer be required for course completion.'
        )) return;
        try {
            await assessmentService.updateAssessment(assessment.id, {
                course: null,
                subject: null,
            });
            toast.success('Assessment removed from course track');
            fetchData();
        } catch (err) {
            toast.error('Failed to unlink assessment');
        }
    };

    const openModal = (type, mode, data = null, subjectId = null) => {
        let suggestedOrder = 1;
        
        if (mode === 'create') {
            if (type === 'subject') {
                const maxOrder = subjects.reduce((max, s) => Math.max(max, s.order || 0), 0);
                suggestedOrder = maxOrder + 1;
            } else if (type === 'lesson' && subjectId) {
                const lessons = lessonsBySubject[subjectId] || [];
                const maxOrder = lessons.reduce((max, l) => Math.max(max, l.order || 0), 0);
                suggestedOrder = maxOrder + 1;
            }
        } else {
            suggestedOrder = data?.order || 1;
        }

        setModalConfig({ 
            isOpen: true, 
            type, 
            mode, 
            data, 
            subjectId,
            suggestedOrder
        });
    };

    const closeModal = () => {
        setModalConfig(prev => ({ ...prev, isOpen: false }));
    };

    if (loading && !course) return (
        <div className="min-h-screen bg-[var(--color-bg-primary)] transition-colors duration-300 flex items-center justify-center">
            <div className="flex flex-col items-center gap-4">
                <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                <p className="text-muted font-medium animate-pulse">Loading curriculum...</p>
            </div>
        </div>
    );

    return (
        <DashboardLayout>
            
            <main className="container max-w-5xl py-xl px-md">
                {/* Header Section */}
                <motion.header 
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-2xl"
                >
                    <button
                        onClick={() => navigate('/admin/courses')}
                        className="group flex items-center gap-2 text-slate-500 hover:text-primary transition-all mb-6 font-medium"
                    >
                        <div className="p-1.5 rounded-lg bg-white shadow-sm group-hover:bg-primary/5 transition-colors">
                            <ArrowLeft size={16} />
                        </div>
                        Back to Courses
                    </button>
                    
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm relative overflow-hidden">
                        <div className="absolute top-0 right-0 p-8 text-primary/5 opacity-20 pointer-events-none">
                            <BookOpen size={120} />
                        </div>
                        
                        <div className="relative z-10">
                            <div className="flex items-center gap-3 mb-2">
                                <span className="px-3 py-1 rounded-full bg-emerald-50 text-emerald-600 text-[10px] font-bold uppercase tracking-wider border border-emerald-100">
                                    Curriculum Editor
                                </span>
                                <span className="text-slate-300">•</span>
                                <span className="text-slate-400 text-sm font-medium">ID: #{id}</span>
                            </div>
                            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-tight">
                                {course?.title}
                            </h2>
                            <p className="text-slate-500 mt-2 flex items-center gap-2">
                                <Layers size={16} className="text-primary" />
                                {subjects.length} Modules • {Object.values(lessonsBySubject).flat().length} Lessons •{' '}
                                {Object.values(assessmentsBySubject).flat().length + courseWideAssessments.length} Assessments
                            </p>
                        </div>
                        
                        <button
                            onClick={() => openModal('subject', 'create')}
                            className="relative group z-10 bg-slate-900 hover:bg-slate-800 text-white px-8 py-4 rounded-2xl font-bold shadow-lg shadow-slate-200 transition-all flex items-center gap-3 active:scale-95"
                        >
                            <div className="p-1 rounded-lg bg-white/10 group-hover:bg-white/20 transition-colors">
                                <Plus size={20} />
                            </div>
                            Add Module
                        </button>
                    </div>
                </motion.header>

                {/* Progress/Summary Bar */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                    <div className="bg-white p-4 rounded-2xl border border-slate-100 flex items-center gap-4">
                        <div className="p-3 rounded-xl bg-teal-50 text-teal-700"><Clock size={20} /></div>
                        <div>
                            <p className="text-xs text-slate-400 font-bold uppercase">Total Duration</p>
                            <p className="text-lg font-bold text-slate-800">
                                {subjects.reduce((sum, s) => sum + (s.duration_minutes || 0), 0)} min
                            </p>
                        </div>
                    </div>
                    <div className="bg-white p-4 rounded-2xl border border-slate-100 flex items-center gap-4">
                        <div className="p-3 rounded-xl bg-emerald-50 text-emerald-600"><CheckCircle2 size={20} /></div>
                        <div>
                            <p className="text-xs text-slate-400 font-bold uppercase">Status</p>
                            <p className="text-lg font-bold text-slate-800">{course?.status || 'Active'}</p>
                        </div>
                    </div>
                    <div className="bg-white p-4 rounded-2xl border border-slate-100 flex items-center gap-4">
                        <div className="p-3 rounded-xl bg-orange-50 text-orange-600"><Layout size={20} /></div>
                        <div>
                            <p className="text-xs text-slate-400 font-bold uppercase">Visibility</p>
                            <p className="text-lg font-bold text-slate-800">Public</p>
                        </div>
                    </div>
                </div>

                {/* Subjects List */}
                <div className="space-y-6">
                    <AnimatePresence mode="popLayout">
                        {subjects.length === 0 && !loading && (
                            <motion.div 
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="bg-white rounded-[2.5rem] p-16 text-center border-2 border-dashed border-slate-200"
                            >
                                <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <BookOpen size={40} className="text-slate-300" />
                                </div>
                                <h3 className="text-xl font-bold text-slate-800 mb-2">Build your curriculum</h3>
                                <p className="text-slate-500 max-w-xs mx-auto mb-8">Start by adding your first module to organize the learning journey.</p>
                                <button
                                    onClick={() => openModal('subject', 'create')}
                                    className="text-primary font-bold hover:bg-primary/5 px-6 py-2 rounded-xl transition-all inline-flex items-center gap-2"
                                >
                                    <Plus size={18} />
                                    Create the first module
                                </button>
                            </motion.div>
                        )}

                        {subjects.map((subject, sIdx) => (
                            <motion.div 
                                key={subject.id}
                                layout
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: sIdx * 0.1 }}
                                className="bg-white rounded-[2rem] shadow-sm border border-slate-100 overflow-hidden"
                            >
                                {/* Subject Header */}
                                <div
                                    className={`p-6 flex items-center justify-between transition-colors cursor-pointer ${expandedSubjects[subject.id] ? 'bg-slate-50/50' : 'hover:bg-slate-50/30'}`}
                                    onClick={() => toggleSubject(subject.id)}
                                >
                                    <div className="flex items-center gap-5">
                                        <div className="flex items-center justify-center w-10 h-10 rounded-2xl bg-slate-900 font-black text-white text-sm shadow-md">
                                            {subject.order || sIdx + 1}
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-xl text-slate-900 group">{subject.title}</h3>
                                            <div className="flex items-center gap-3 mt-1">
                                                <span className="text-xs text-slate-400 font-bold uppercase tracking-widest bg-white border border-slate-100 px-2 py-0.5 rounded-md">
                                                    {lessonsBySubject[subject.id]?.length || 0} Lessons
                                                </span>
                                                <span className="text-xs text-slate-400 font-bold uppercase tracking-widest bg-white border border-slate-100 px-2 py-0.5 rounded-md">
                                                    {(assessmentsBySubject[subject.id] || []).length} Assessments
                                                </span>
                                                <span className="text-slate-200">|</span>
                                                <span className="text-xs text-slate-400 flex items-center gap-1 font-medium">
                                                    <Clock size={12} /> {subject.duration_minutes || 0} min
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <div className="flex gap-2">
                                            <button
                                                onClick={(e) => { e.stopPropagation(); openModal('subject', 'edit', subject); }}
                                                className="p-2.5 rounded-xl text-slate-400 hover:text-primary hover:bg-white hover:shadow-sm border border-transparent hover:border-slate-100 transition-all"
                                            >
                                                <Edit2 size={18} />
                                            </button>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleDeleteSubject(subject.id); }}
                                                className="p-2.5 rounded-xl text-slate-400 hover:text-red-500 hover:bg-white hover:shadow-sm border border-transparent hover:border-slate-100 transition-all"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                        <div className={`p-2 rounded-xl bg-slate-100/50 text-slate-400 transition-transform duration-300 ${expandedSubjects[subject.id] ? 'rotate-180' : ''}`}>
                                            <ChevronDown size={20} />
                                        </div>
                                    </div>
                                </div>

                                {/* Lessons Content */}
                                <AnimatePresence>
                                    {expandedSubjects[subject.id] && (
                                        <motion.div 
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            className="overflow-hidden"
                                        >
                                            <div className="border-t border-slate-50 p-6 space-y-3 bg-[#fafcfd]">
                                                {lessonsBySubject[subject.id]?.map((lesson, lIdx) => (
                                                    <motion.div
                                                        key={lesson.id}
                                                        initial={{ opacity: 0, y: 10 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex items-center justify-between group hover:border-primary/20 hover:shadow-md hover:shadow-primary/5 transition-all"
                                                    >
                                                        <div className="flex items-center gap-4">
                                                            <div className="text-slate-300 group-hover:text-slate-400 cursor-grab px-1">
                                                                <GripVertical size={16} />
                                                            </div>
                                                            <div className={`p-3 rounded-xl ${
                                                                lesson.content_type === 'video' ? 'bg-orange-50 text-orange-600' :
                                                                lesson.content_type === 'pdf' ? 'bg-red-50 text-red-600' :
                                                                'bg-teal-50 text-teal-700'
                                                            }`}>
                                                                {lesson.content_type === 'video' ? <Video size={20} /> :
                                                                    lesson.content_type === 'pdf' ? <File size={20} /> :
                                                                        <FileText size={20} />}
                                                            </div>
                                                            <div>
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-[10px] font-black text-slate-300 bg-slate-50 px-1.5 rounded uppercase">L-{lesson.order || lIdx + 1}</span>
                                                                    <h4 className="font-bold text-slate-800">{lesson.title}</h4>
                                                                </div>
                                                                <div className="flex items-center gap-2 mt-0.5">
                                                                    <span className="text-xs text-slate-400 font-medium">{lesson.duration_minutes} min</span>
                                                                    <span className="text-slate-200">•</span>
                                                                    <span className="text-xs text-slate-400 uppercase font-bold text-[9px] tracking-widest">{lesson.content_type} Content</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-all translate-x-2 group-hover:translate-x-0">
                                                            <button
                                                                onClick={() => openModal('lesson', 'edit', lesson, subject.id)}
                                                                className="p-2 rounded-xl text-slate-400 hover:text-primary hover:bg-slate-50 transition-all"
                                                            >
                                                                <Edit2 size={16} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteLesson(lesson.id, subject.id)}
                                                                className="p-2 rounded-xl text-slate-400 hover:text-red-500 hover:bg-slate-50 transition-all"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </div>
                                                    </motion.div>
                                                ))}

                                                {(assessmentsBySubject[subject.id] || []).map((assessment) => (
                                                    <motion.div
                                                        key={`a-${assessment.id}`}
                                                        initial={{ opacity: 0, y: 10 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        className="bg-white p-4 rounded-2xl border border-violet-100 shadow-sm flex items-center justify-between group hover:border-violet-300 hover:shadow-md transition-all"
                                                    >
                                                        <div className="flex items-center gap-4">
                                                            <div className="p-3 rounded-xl bg-violet-50 text-violet-700">
                                                                <ClipboardCheck size={20} />
                                                            </div>
                                                            <div>
                                                                <div className="flex items-center gap-2 flex-wrap">
                                                                    <span className="text-[10px] font-black text-violet-400 bg-violet-50 px-1.5 rounded uppercase">
                                                                        {assessment.assessment_type}
                                                                    </span>
                                                                    <h4 className="font-bold text-slate-800">{assessment.title}</h4>
                                                                    {assessment.is_active !== false && (
                                                                        <span className="text-[9px] font-black uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-100 px-2 py-0.5 rounded-full">
                                                                            Required for completion
                                                                        </span>
                                                                    )}
                                                                    {assessment.is_active === false && (
                                                                        <span className="text-[9px] font-black uppercase tracking-wider bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">
                                                                            Inactive
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div className="flex items-center gap-2 mt-0.5">
                                                                    <span className="text-xs text-slate-400 font-medium">
                                                                        Pass {assessment.passing_score}%
                                                                    </span>
                                                                    <span className="text-slate-200">•</span>
                                                                    <span className="text-xs text-slate-400 uppercase font-bold text-[9px] tracking-widest">
                                                                        Assessment
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-all">
                                                            <button
                                                                onClick={() => navigate(`/admin/assessments/${assessment.id}`)}
                                                                className="p-2 rounded-xl text-slate-400 hover:text-primary hover:bg-slate-50 transition-all"
                                                                aria-label={`Edit assessment ${assessment.title}`}
                                                            >
                                                                <Edit2 size={16} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleUnlinkAssessment(assessment)}
                                                                className="p-2 rounded-xl text-slate-400 hover:text-red-500 hover:bg-slate-50 transition-all"
                                                                aria-label={`Remove ${assessment.title} from course`}
                                                                title="Remove from course track"
                                                            >
                                                                <Unlink size={16} />
                                                            </button>
                                                        </div>
                                                    </motion.div>
                                                ))}

                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                                                    <button
                                                        onClick={() => openModal('lesson', 'create', null, subject.id)}
                                                        className="w-full py-4 rounded-2xl border-2 border-dashed border-slate-200 text-slate-400 hover:text-primary hover:border-primary/50 hover:bg-primary/5 transition-all flex items-center justify-center gap-3 text-sm font-bold"
                                                    >
                                                        <Plus size={16} />
                                                        Add Lesson
                                                    </button>
                                                    <button
                                                        onClick={() => openModal('assessment', 'create', null, subject.id)}
                                                        className="w-full py-4 rounded-2xl border-2 border-dashed border-violet-200 text-violet-500 hover:text-violet-700 hover:border-violet-400 hover:bg-violet-50 transition-all flex items-center justify-center gap-3 text-sm font-bold"
                                                    >
                                                        <ClipboardCheck size={16} />
                                                        Add Assessment
                                                    </button>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </motion.div>
                        ))}
                    </AnimatePresence>

                    {courseWideAssessments.length > 0 && (
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-white rounded-[2rem] shadow-sm border border-violet-100 overflow-hidden p-6 space-y-3"
                        >
                            <div className="flex items-center gap-3 mb-2">
                                <ClipboardCheck size={18} className="text-violet-600" />
                                <div>
                                    <h3 className="font-bold text-lg text-slate-900">Course-wide assessments</h3>
                                    <p className="text-xs text-slate-400 font-medium">
                                        Linked to this course but not placed under a specific module. Still required for completion while active.
                                    </p>
                                </div>
                            </div>
                            {courseWideAssessments.map((assessment) => (
                                <div
                                    key={`cw-${assessment.id}`}
                                    className="bg-[#fafcfd] p-4 rounded-2xl border border-violet-100 flex items-center justify-between"
                                >
                                    <div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <h4 className="font-bold text-slate-800">{assessment.title}</h4>
                                            <span className="text-[9px] font-black uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-100 px-2 py-0.5 rounded-full">
                                                Required for completion
                                            </span>
                                        </div>
                                        <p className="text-xs text-slate-400 mt-1 uppercase font-bold tracking-widest">
                                            {assessment.assessment_type} · Pass {assessment.passing_score}%
                                        </p>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => navigate(`/admin/assessments/${assessment.id}`)}
                                            className="p-2 rounded-xl text-slate-400 hover:text-primary hover:bg-white transition-all"
                                            aria-label={`Edit ${assessment.title}`}
                                        >
                                            <Edit2 size={16} />
                                        </button>
                                        <button
                                            onClick={() => handleUnlinkAssessment(assessment)}
                                            className="p-2 rounded-xl text-slate-400 hover:text-red-500 hover:bg-white transition-all"
                                            aria-label={`Unlink ${assessment.title}`}
                                        >
                                            <Unlink size={16} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </motion.div>
                    )}
                </div>
            </main>

            {/* Modal */}
            <AnimatePresence>
                {modalConfig.isOpen && (
                    <ContentModal
                        config={modalConfig}
                        onClose={closeModal}
                        onSuccess={() => { closeModal(); fetchData(); }}
                        courseId={id}
                    />
                )}
            </AnimatePresence>
        </DashboardLayout>
    );
}

function ContentModal({ config, onClose, onSuccess, courseId }) {
    const navigate = useNavigate();
    const { type, mode, data, subjectId, suggestedOrder } = config;
    const [saving, setSaving] = useState(false);
    const [linkableAssessments, setLinkableAssessments] = useState([]);
    const [linkMode, setLinkMode] = useState('create'); // 'create' | 'link'

    const [formData, setFormData] = useState({
        title: data?.title || '',
        description: data?.description || '',
        order: data?.order || suggestedOrder || 1,
        content_type: data?.content_type || 'text',
        content: data?.content || '',
        duration_minutes: data?.duration_minutes || 0,
        is_active: data?.is_active ?? true,
        assessment_type: 'quiz',
        passing_score: 70,
        existing_assessment_id: '',
        is_required: true,
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (type !== 'assessment') return;
        let cancelled = false;
        const loadLinkable = async () => {
            try {
                const [standaloneRes, courseRes] = await Promise.all([
                    assessmentService.getAssessments({ page_size: 100 }),
                    assessmentService.getAssessments({ course: courseId, page_size: 100 }),
                ]);
                const all = [
                    ...(standaloneRes.data.results || standaloneRes.data || []),
                    ...(courseRes.data.results || courseRes.data || []),
                ];
                const seen = new Set();
                const options = all.filter((a) => {
                    if (seen.has(a.id)) return false;
                    seen.add(a.id);
                    const aCourse = a.course || a.course_id;
                    // Allow linking practice assessments, or re-placing ones already on this course
                    return !aCourse || String(aCourse) === String(courseId);
                });
                if (!cancelled) setLinkableAssessments(options);
            } catch {
                if (!cancelled) setLinkableAssessments([]);
            }
        };
        loadLinkable();
        return () => { cancelled = true; };
    }, [type, courseId]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setErrors({});
        try {
            if (type === 'subject') {
                const payload = { ...formData, course: courseId };
                if (mode === 'create') await courseService.createSubject(payload);
                else await courseService.updateSubject(data.id, payload);
            } else if (type === 'lesson') {
                const payload = { ...formData, subject: subjectId };
                if (mode === 'create') await courseService.createLesson(payload);
                else await courseService.updateLesson(data.id, payload);
            } else if (type === 'assessment') {
                if (linkMode === 'link') {
                    if (!formData.existing_assessment_id) {
                        toast.error('Select an assessment to place on this module.');
                        setSaving(false);
                        return;
                    }
                    // Placing on a course module makes it required for completion while active.
                    await assessmentService.updateAssessment(formData.existing_assessment_id, {
                        course: formData.is_required ? courseId : null,
                        subject: formData.is_required ? subjectId : null,
                    });
                    if (!formData.is_required) {
                        toast.success('Assessment kept as standalone practice (not required).');
                    } else {
                        toast.success('Assessment placed on this module and marked required for completion.');
                    }
                    onSuccess();
                    setSaving(false);
                    return;
                }

                const now = new Date();
                const end = new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000);
                const payload = {
                    title: formData.title,
                    description: formData.description || `Assessment for this module`,
                    assessment_type: formData.assessment_type,
                    course: formData.is_required ? courseId : null,
                    subject: formData.is_required ? subjectId : null,
                    start_date: now.toISOString(),
                    end_date: end.toISOString(),
                    time_limit_minutes: 30,
                    max_attempts: 3,
                    total_points: 100,
                    passing_score: formData.passing_score || 70,
                    is_active: true,
                    auto_grade: true,
                };
                const res = await assessmentService.createAssessment(payload);
                toast.success(
                    formData.is_required
                        ? 'Assessment created and required for course completion.'
                        : 'Standalone practice assessment created.'
                );
                onSuccess();
                if (res?.data?.id) {
                    navigate(`/admin/assessments/${res.data.id}`);
                }
                setSaving(false);
                return;
            }
            toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} ${mode === 'create' ? 'created' : 'updated'} successfully!`);
            onSuccess();
        } catch (err) {
            const errorData = err.response?.data;
            if (errorData && typeof errorData === 'object') {
                // Check for unique_together error
                if (errorData.non_field_errors?.some(msg => msg.includes('unique set'))) {
                    setErrors({ order: 'This order number is already taken in this course.' });
                    toast.error('Order number conflict identified.');
                } else {
                    setErrors(errorData);
                    toast.error('Please fix the errors in the form.');
                }
            } else {
                toast.error(`Failed to ${mode} ${type}.`);
            }
        } finally {
            setSaving(false);
        }
    };

    const modalTitle = type === 'subject'
        ? 'Module'
        : type === 'lesson'
            ? 'Lesson'
            : 'Assessment';

    return (
        <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-md"
        >
            <motion.div 
                initial={{ scale: 0.9, y: 20, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                exit={{ scale: 0.9, y: 20, opacity: 0 }}
                className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-2xl overflow-hidden border border-slate-100"
            >
                <div className="p-8 border-b border-slate-100 flex justify-between items-center bg-[#fdfdfe]">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            {type === 'subject' ? <Layers size={16} className="text-primary" /> : type === 'assessment' ? <ClipboardCheck size={16} className="text-violet-600" /> : <BookOpen size={16} className="text-teal-600" />}
                            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
                                Curriculum Builder
                            </span>
                        </div>
                        <h3 className="text-2xl font-black text-slate-900 tracking-tight">
                            {mode === 'create' ? 'Create New' : 'Edit Existing'} {modalTitle}
                        </h3>
                    </div>
                    <button 
                        onClick={onClose} 
                        className="p-3 rounded-2xl bg-slate-50 text-slate-400 hover:bg-red-50 hover:text-red-500 transition-all"
                    >
                        <X size={20} />
                    </button>
                </div>
                
                <form onSubmit={handleSubmit} className="p-8 space-y-6 max-h-[70vh] overflow-y-auto custom-scrollbar">
                    {type === 'assessment' && (
                        <>
                            <div className="flex gap-2 p-1 bg-slate-100 rounded-2xl">
                                <button
                                    type="button"
                                    onClick={() => setLinkMode('create')}
                                    className={`flex-1 py-3 rounded-xl text-xs font-black uppercase tracking-wider transition-all ${linkMode === 'create' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
                                >
                                    Create new
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setLinkMode('link')}
                                    className={`flex-1 py-3 rounded-xl text-xs font-black uppercase tracking-wider transition-all ${linkMode === 'link' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
                                >
                                    Place existing
                                </button>
                            </div>

                            <div className="flex items-center gap-4 bg-violet-50 p-4 rounded-2xl border border-violet-100">
                                <div className="w-10 h-10 rounded-xl bg-white border border-violet-100 flex items-center justify-center">
                                    <CheckCircle2 size={18} className={formData.is_required ? 'text-emerald-500' : 'text-slate-300'} />
                                </div>
                                <div className="flex-1">
                                    <p className="text-sm font-bold text-slate-800">Required for course completion</p>
                                    <p className="text-xs text-slate-500 font-medium">
                                        When on, learners must pass this assessment before the course can be marked complete and a certificate issued.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, is_required: !formData.is_required })}
                                    className={`w-14 h-8 rounded-full transition-all relative ${formData.is_required ? 'bg-emerald-500 shadow-lg shadow-emerald-200' : 'bg-slate-300'}`}
                                    aria-label="Toggle required for completion"
                                    aria-pressed={formData.is_required}
                                >
                                    <motion.div 
                                        animate={{ x: formData.is_required ? 28 : 4 }}
                                        className="absolute top-1 left-0 w-6 h-6 bg-white rounded-full shadow-sm"
                                    />
                                </button>
                            </div>

                            {linkMode === 'link' ? (
                                <div className="space-y-2">
                                    <label className="text-[11px] font-black text-slate-400 uppercase tracking-wider ml-1">Assessment</label>
                                    <AppSelect
                                        required
                                        className="w-full px-4 py-4 rounded-2xl border border-slate-100 bg-slate-50/50 focus:bg-white focus:border-primary outline-none font-bold text-slate-800"
                                        value={formData.existing_assessment_id}
                                        onChange={(e) => setFormData({ ...formData, existing_assessment_id: e.target.value })}
                                    >
                                        <option value="">Select an assessment…</option>
                                        {linkableAssessments.map((a) => (
                                            <option key={a.id} value={a.id}>
                                                {a.title} ({a.assessment_type}){(a.course || a.course_id) ? ' · course-linked' : ' · practice'}
                                            </option>
                                        ))}
                                    </AppSelect>
                                </div>
                            ) : (
                                <>
                                    <div className="space-y-2">
                                        <label className="text-[11px] font-black text-slate-400 uppercase tracking-wider ml-1">Title</label>
                                        <input
                                            required
                                            placeholder="E.g. Getting Started Quiz"
                                            className="w-full px-4 py-4 rounded-2xl border border-slate-100 bg-slate-50/50 focus:bg-white focus:border-primary outline-none font-bold text-slate-800"
                                            value={formData.title}
                                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                        />
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="space-y-2">
                                            <label className="text-[11px] font-black text-slate-400 uppercase tracking-wider ml-1">Type</label>
                                            <AppSelect
                                                className="w-full px-4 py-4 rounded-2xl border border-slate-100 bg-slate-50/50 font-bold text-slate-800"
                                                value={formData.assessment_type}
                                                onChange={(e) => setFormData({ ...formData, assessment_type: e.target.value })}
                                            >
                                                <option value="quiz">Quiz</option>
                                                <option value="test">Test</option>
                                                <option value="exam">Exam</option>
                                                <option value="assignment">Assignment</option>
                                            </AppSelect>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[11px] font-black text-slate-400 uppercase tracking-wider ml-1">Passing score (%)</label>
                                            <input
                                                type="number"
                                                min="1"
                                                max="100"
                                                required
                                                className="w-full px-4 py-4 rounded-2xl border border-slate-100 bg-slate-50/50 font-bold text-slate-800"
                                                value={formData.passing_score}
                                                onChange={(e) => setFormData({ ...formData, passing_score: parseInt(e.target.value, 10) || 70 })}
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[11px] font-black text-slate-400 uppercase tracking-wider ml-1">Instructions</label>
                                        <textarea
                                            rows={3}
                                            className="w-full px-4 py-4 rounded-2xl border border-slate-100 bg-slate-50/50 font-bold text-slate-800 resize-none"
                                            value={formData.description}
                                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        />
                                    </div>
                                </>
                            )}
                        </>
                    )}

                    {type !== 'assessment' && (
                    <>
                    {/* Unique Set Error Banner */}
                    {errors.order && (
                        <div className="p-4 bg-red-50 rounded-2xl border border-red-100 flex items-start gap-3 animate-in fade-in slide-in-from-top-2">
                            <AlertCircle className="text-red-500 shrink-0 mt-0.5" size={18} />
                            <div>
                                <p className="text-sm font-bold text-red-900">Sequence Conflict</p>
                                <p className="text-xs text-red-600 font-medium">This position {formData.order} is already occupied. Please use a different order number.</p>
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label className="text-[11px] font-black text-slate-400 uppercase tracking-wider ml-1">Title</label>
                            <div className="relative">
                                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300">
                                    {type === 'subject' ? <Type size={18} /> : <BookOpen size={18} />}
                                </span>
                                <input
                                    required
                                    placeholder={type === 'subject' ? 'E.g. Introduction to Python' : 'E.g. Your First Script'}
                                    className={`w-full pl-12 pr-4 py-4 rounded-2xl border ${errors.title ? 'border-red-200 bg-red-50' : 'border-slate-100 bg-slate-50/50'} focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/5 outline-none transition-all font-bold text-slate-800`}
                                    value={formData.title}
                                    onChange={e => setFormData({ ...formData, title: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-[11px] font-black text-slate-400 uppercase tracking-wider ml-1">Sequence Position (Order)</label>
                            <div className="relative">
                                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300">
                                    <Layers size={18} />
                                </span>
                                <input
                                    type="number"
                                    required
                                    min="1"
                                    className={`w-full pl-12 pr-4 py-4 rounded-2xl border ${errors.order ? 'border-red-200 bg-red-50' : 'border-slate-100 bg-slate-50/50'} focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/5 outline-none transition-all font-bold text-slate-800`}
                                    value={formData.order}
                                    onChange={e => setFormData({ ...formData, order: parseInt(e.target.value) })}
                                />
                                {mode === 'create' && (
                                    <div className="absolute right-4 top-1/2 -translate-y-1/2">
                                        <span className="text-[9px] font-black bg-emerald-50 text-emerald-600 px-2 py-1 rounded-lg uppercase tracking-wider">Suggested</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label className="text-[11px] font-black text-slate-400 uppercase tracking-wider ml-1">
                                {type === 'lesson' ? 'Content Type' : 'Estimated Duration'}
                            </label>
                            {type === 'lesson' ? (
                                <AppSelect
                                    className="w-full px-4 py-4 rounded-2xl border border-slate-100 bg-slate-50/50 focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/5 outline-none transition-all font-bold text-slate-800 appearance-none"
                                    value={formData.content_type}
                                    onChange={e => setFormData({ ...formData, content_type: e.target.value })}
                                >
                                    <option value="text">📄 Rich Text / HTML</option>
                                    <option value="video">🎥 Video Content</option>
                                    <option value="pdf">📕 PDF Document</option>
                                    <option value="markdown">📝 Markdown Editor</option>
                                </AppSelect>
                            ) : (
                                <div className="relative">
                                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300">
                                        <Clock size={18} />
                                    </span>
                                    <input
                                        type="number"
                                        placeholder="Minutes"
                                        className="w-full pl-12 pr-4 py-4 rounded-2xl border border-slate-100 bg-slate-50/50 focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/5 outline-none transition-all font-bold text-slate-800"
                                        value={formData.duration_minutes}
                                        onChange={e => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
                                    />
                                </div>
                            )}
                        </div>
                        
                        {type === 'lesson' && (
                            <div className="space-y-2">
                                <label className="text-[11px] font-black text-slate-400 uppercase tracking-wider ml-1">Lesson Duration (min)</label>
                                <div className="relative">
                                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300">
                                        <Clock size={18} />
                                    </span>
                                    <input
                                        type="number"
                                        className="w-full pl-12 pr-4 py-4 rounded-2xl border border-slate-100 bg-slate-50/50 focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/5 outline-none transition-all font-bold text-slate-800"
                                        value={formData.duration_minutes}
                                        onChange={e => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="space-y-2">
                        <label className="text-[11px] font-black text-slate-400 uppercase tracking-wider ml-1">Description / Summary</label>
                        <textarea
                            rows={3}
                            placeholder="Briefly describe what students will learn..."
                            className="w-full px-4 py-4 rounded-2xl border border-slate-100 bg-slate-50/50 focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/5 outline-none transition-all font-bold text-slate-800 resize-none"
                            value={formData.description}
                            onChange={e => setFormData({ ...formData, description: e.target.value })}
                        />
                    </div>

                    {type === 'lesson' && formData.content_type === 'text' && (
                        <div className="space-y-2">
                            <label className="text-[11px] font-black text-slate-400 uppercase tracking-wider ml-1">Lesson Content</label>
                            <textarea
                                rows={6}
                                className="w-full px-4 py-4 rounded-2xl border border-slate-100 bg-slate-50/50 focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/5 outline-none transition-all font-bold text-slate-800 resize-none font-mono text-sm shadow-inner"
                                value={formData.content}
                                onChange={e => setFormData({ ...formData, content: e.target.value })}
                                placeholder="Enter lesson text or HTML..."
                            />
                        </div>
                    )}

                    <div className="flex items-center gap-4 bg-slate-50 p-4 rounded-2xl border border-slate-100">
                        <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center">
                            <CheckCircle2 size={18} className={formData.is_active ? 'text-emerald-500' : 'text-slate-300'} />
                        </div>
                        <div className="flex-1">
                            <p className="text-sm font-bold text-slate-800">Module Visibility</p>
                            <p className="text-xs text-slate-400 font-medium">Toggle whether this content is visible to students.</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                            className={`w-14 h-8 rounded-full transition-all relative ${formData.is_active ? 'bg-primary shadow-lg shadow-primary/20' : 'bg-slate-300'}`}
                        >
                            <motion.div 
                                animate={{ x: formData.is_active ? 28 : 4 }}
                                className="absolute top-1 left-0 w-6 h-6 bg-white rounded-full shadow-sm"
                            />
                        </button>
                    </div>
                    </>
                    )}

                    <div className="flex flex-col md:flex-row justify-end gap-3 pt-4 border-t border-slate-50">
                        <button 
                            type="button" 
                            onClick={onClose} 
                            disabled={saving}
                            className="px-8 py-3 rounded-2xl font-black text-[11px] uppercase tracking-widest text-slate-500 hover:bg-slate-50 transition-all border border-transparent hover:border-slate-100 disabled:opacity-50"
                        >
                            Close Editor
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="px-10 py-4 rounded-2xl font-black text-[11px] uppercase tracking-widest bg-slate-900 text-white shadow-xl shadow-slate-200 hover:shadow-primary/20 hover:bg-slate-800 flex items-center justify-center gap-3 transition-all active:scale-95 disabled:opacity-50"
                        >
                            {saving ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Save size={16} />
                                    {type === 'assessment'
                                        ? (linkMode === 'link' ? 'Place on Module' : 'Create Assessment')
                                        : (mode === 'create' ? `Create ${modalTitle}` : 'Commit Changes')}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </motion.div>
        </motion.div>
    );
}

export default CourseContent;
